import * as signalR from '@microsoft/signalr';
import { TokenService } from '../token/token.service';
import { AuthService } from '../auth/auth.service';
import { Injectable } from '@angular/core';
import {
  Observable,
  Subject,
  catchError,
  defer,
  of,
  skipUntil,
  switchMap,
  take,
  throwError,
} from 'rxjs';

export interface XMessageHandler {
  method: string;
  callback: () => void;
}

@Injectable()
export class WebSocketService {
  constructor(
    private _tokenService: TokenService,
    private _authService: AuthService,
  ) {}

  newInstance() {
    return new WebSocketHubConnection(this._tokenService, this._authService);
  }
}

export class WebSocketOptions {
  accessTokenFactory?(): string;
  refreshTokenFactory?(): Observable<string>;
}

export class WebSocketHubConnection {
  private _hubConnection: signalR.HubConnection;
  private _webSocketConnection = new Subject<boolean>();

  constructor(
    private _tokenService: TokenService,
    private _authService: AuthService,
  ) {}

  /**
   * Invokes a specified hub method after establishing a WebSocket connection to the provided URL.
   *
   * @param connectionMessage - The hub method to invoke after establishing the connection.
   *                            This method typically handles specific actions or requests on the server side.
   * @param payload - The data payload to be sent along with the connection message.
   *@returns An Observable that represents the invocation of the specified hub method.
   *          The Observable completes when the invocation is successful.
   */
  invoke(connectionMessage: string, payload: any = null): Observable<void> {
    const invokeFunction = defer(async () => {
      await this._hubConnection.invoke(connectionMessage, payload ?? {});
    }).pipe(take(1));

    if (this._hubConnection.state === signalR.HubConnectionState.Connected) {
      return invokeFunction;
    }

    // Socket connection needs to be established before invokingFunction
    return this._webSocketConnection.pipe(
      skipUntil(this._webSocketConnection),
      switchMap(() => invokeFunction),
    );
  }

  /**
   * Registers message handlers with the corresponding callback function.
   *
   * @param message - The hub method to invoke after establishing the connection.
   * @param handler - Callback function to handle incoming messages.
   */
  on(message: string, handler: () => void) {
    this._hubConnection.on(message, handler);
  }

  /**
   * Stops the connection.
   *
   * @returns {Observable<void>} An observable that resolves when the connection has been successfully terminated, or rejects with an error.
   */
  stop(): Observable<void> {
    return defer(async () => await this._hubConnection?.stop()).pipe(take(1));
  }

  /**
   * Unsubscribes from the connection.
   * This method removes the specified message handler and stops listening for messages of the given type on the connection.
   *
   * @param message - The message handler to unsubscribe from.
   *                  When a message of this type is received on the SignalR hub connection, the corresponding handler will no longer be invoked.
   */
  off(message: string): void {
    this._hubConnection?.off(message);
  }

  /**
   * Initiates the connection.
   *
   * @param url - The WebSocket endpoint URL to establish the hub connection.
   * @param connectionOptions - Instructions on how to refresh a token.
   * @param shouldRetry - Whether a reconnect attempt should be performed or not when an issue occurs.
   * @returns {Observable<void>} An observable that resolves when the connection has been successfully established, or rejects with an error.
   */
  start(
    url: string,
    connectionOptions: WebSocketOptions = null,
    shouldRetry: boolean = true,
  ): Observable<void> {
    return this._start(url, connectionOptions).pipe(
      catchError((err: unknown) => {
        if (shouldRetry) {
          if (connectionOptions?.refreshTokenFactory) {
            this._refreshToken = connectionOptions.refreshTokenFactory;

            return this._refreshToken().pipe(
              switchMap(() => {
                return this.start(url, connectionOptions, false);
              }),
            );
          }
        } else {
          return throwError(() => err);
        }
      }),
      take(1),
    );
  }

  private _start(url: string, connectionOptions: WebSocketOptions): Observable<void> {
    if (
      this._hubConnection?.baseUrl === url &&
      this._hubConnection.state == signalR.HubConnectionState.Connected
    ) {
      this._hubConnection.stop();
    }

    const options: signalR.IHttpConnectionOptions = {
      skipNegotiation: true,
      transport: signalR.HttpTransportType.WebSockets,
      accessTokenFactory: () => {
        return this._tokenService.getToken();
      },
    };

    if (connectionOptions?.accessTokenFactory) {
      options.accessTokenFactory = connectionOptions.accessTokenFactory;
    }

    const retryDelays = [0, 500, 1500];
    this._hubConnection = new signalR.HubConnectionBuilder()
      .withUrl(url, options)
      .withAutomaticReconnect(retryDelays)
      .build();

    this._hubConnection.onreconnecting(() => {
      console.log('socket onreconnecting');
    });

    this._hubConnection.onreconnected(() => {
      this._webSocketConnection.next(true);
      console.log('socket reconnected');
    });

    this._hubConnection.onclose(() => {
      console.log('socket closed');
    });

    return defer(async () => {
      await this._hubConnection.start();
      this._webSocketConnection.next(true);
    });
  }

  private _refreshToken(): Observable<string> {
    return this._authService.refreshToken(this._tokenService.getToken()).pipe(
      switchMap((newToken) => {
        this._tokenService.setToken(newToken);
        return of(newToken);
      }),
    );
  }
}
